import { formatMoney } from '@betterment/core';
import { ListRow, Text } from '@betterment/samba-react';
import { useColorMode } from '@betterment/shared-components/src/components/color-mode/ColorModeProvider';

import { CheckingUnavailableRow } from './CheckingUnavailableRow';
import { LeadingIcon } from './LeadingIcon';

import { DebitCardLockTypeCode, JointAccountInvitationStatus } from '../../../graphql/schema';
import { StyledLinkRow, titleCss } from '../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { CheckingAccountFragment as CheckingAccount } from '../graphql/query.envelopesCard.generated';

const JOINT_INVITE_STATUSES = [
  JointAccountInvitationStatus.AccountCreated,
  JointAccountInvitationStatus.InviteSent,
  JointAccountInvitationStatus.SecondaryInKyc,
  JointAccountInvitationStatus.SecondaryKycVerified,
];

const statusDescription = (remote: NonNullable<CheckingAccount['remote']>) => {
  const { customer, sentInvitations } = remote;

  const isDebitCardLocked = customer.card && customer.card.lockTypeCode === DebitCardLockTypeCode.SystemLocked;
  const hasJointInvitePending = sentInvitations.some(invitation => JOINT_INVITE_STATUSES.includes(invitation.status));

  if (isDebitCardLocked) {
    return (
      <Text color="text.critical" variant="text50">
        Debit card locked
      </Text>
    );
  }

  if (hasJointInvitePending) {
    return (
      <Text color="text.weak" variant="text50">
        Joint invite pending
      </Text>
    );
  }

  return null;
};

export const CheckingAccountRow: React.FC<{ account: CheckingAccount }> = ({ account }) => {
  const { colorMode } = useColorMode();
  const { remote } = account;
  const iconUrl = account.icon[colorMode];

  if (!remote) return CheckingUnavailableRow({ iconUrl });

  const { accountId, name, balance } = remote;

  return (
    <StyledLinkRow to={urlBuilder.getAppCheckingSummary.absolute({ accountId })} name={name}>
      <ListRow.LeadingAsset>
        <LeadingIcon url={iconUrl} />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text color="text" css={titleCss}>
          {name}
        </Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Text color="text">{formatMoney(balance)}</Text>
        {statusDescription(remote)}
      </ListRow.TrailingContent>
    </StyledLinkRow>
  );
};
